import React, { useState, useEffect } from 'react';
import { IconSvg } from '../IconSvg';
import { useConfig, useConfigJapanese } from '../../contexts/ConfigContext';
import { TranslateService } from '../../services/japanese';
import { ToolbarButton } from './ToolbarButton';
import { TranslateServiceSelector } from '../TranslateServiceSelector';
import { MobileMenu } from '../MobileMenu';
import { TranslateServiceContent } from '../TranslateServiceSelector';
import './style.css';

interface ToolbarProps {
  onShowGojuon?: () => void;
  onShowSettings?: () => void;
  isDetailOpen?: boolean;
}

export const Toolbar: React.FC<ToolbarProps> = ({ onShowGojuon, onShowSettings, isDetailOpen = false }) => {
  const { config, setConfig } = useConfig();
  const { config: japaneseConfig, updateConfig: updateJapaneseConfig } = useConfigJapanese();
  const [showServiceMenu, setShowServiceMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 检测设备类型
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleServiceChange = (service: TranslateService) => {
    updateJapaneseConfig({ translateService: service });
  };

  const toggleTheme = () => {
    setConfig({ ...config, theme: config.theme === 'light' ? 'dark' : 'light' });
  };

  return (
    <>
      <div className={`toolbar ${isMobile && isDetailOpen ? 'hidden' : ''}`}>
        <div className="toolbar-container">
          <div className="toolbar-group">
            <ToolbarButton
              active={config.showHiragana}
              onClick={() => setConfig({ ...config, showHiragana: !config.showHiragana })}
              tooltip={{
                title: "假名标注",
                description: "显示或隐藏汉字的假名读音"
              }}
            >
              <IconSvg name="mdi:syllabary-hiragana" width={20} height={20} />
            </ToolbarButton>

            <ToolbarButton
              active={config.showRomaji}
              onClick={() => setConfig({ ...config, showRomaji: !config.showRomaji })}
              tooltip={{
                title: "罗马音标注",
                description: "显示或隐藏日语的罗马音拼写"
              }}
            >
              <IconSvg name="tabler:letter-a" width={20} height={20} />
            </ToolbarButton>

            <div className="toolbar-separator" />

            <ToolbarButton
              active={config.showTraditional}
              onClick={() => setConfig({ ...config, showTraditional: !config.showTraditional })}
              tooltip={{
                title: "繁体中文",
                description: "显示或隐藏繁体中文翻译"
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 500 }}>繁</span>
            </ToolbarButton>

            {/* PC端使用原始组件，移动端使用内联实现 */}
            {!isMobile ? (
              <TranslateServiceSelector />
            ) : (
              <ToolbarButton
                onClick={() => setShowServiceMenu(!showServiceMenu)}
                active={showServiceMenu}
                hasExpandAction={true}
                tooltip={{
                  title: "切换翻译服务",
                  description: "选择使用的翻译服务提供商"
                }}
              >
                <IconSvg name="heroicons-solid:switch-horizontal" width={20} height={20} />
              </ToolbarButton>
            )}

            {/* 移动端额外按钮 */}
            {isMobile && (
              <>
                <div className="toolbar-separator" />

                <ToolbarButton
                  onClick={onShowGojuon}
                  tooltip={{
                    title: "五十音图",
                    description: "查看日语五十音图"
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>音</span>
                </ToolbarButton>

                <ToolbarButton
                  onClick={toggleTheme}
                  tooltip={{
                    title: config.theme === 'light' ? "切换到暗色模式" : "切换到亮色模式",
                    description: "切换深色/浅色主题"
                  }}
                >
                  <IconSvg
                    name={config.theme === 'light' ? 'ph:moon-stars' : 'ph:sun'}
                    width={20}
                    height={20}
                  />
                </ToolbarButton>

                <ToolbarButton
                  onClick={onShowSettings}
                  tooltip={{
                    title: "设置",
                    description: "打开设置面板"
                  }}
                >
                  <IconSvg name="mdi:cog" width={20} height={20} />
                </ToolbarButton>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 移动端翻译服务菜单 */}
      {isMobile && showServiceMenu && (
        <MobileMenu
          isOpen={showServiceMenu}
          onClose={() => setShowServiceMenu(false)}
          title="翻译服务"
        >
          <TranslateServiceContent
            service={japaneseConfig.translateService}
            onChange={handleServiceChange}
            closeMenu={() => setShowServiceMenu(false)}
          />
        </MobileMenu>
      )}
    </>
  );
};